import { LitElement, html, css } from 'lit';

const AUDIO_PLAY_EVENT = 'crusher:audio:play';
const AUDIO_PAUSE_EVENT = 'crusher:audio:pause';
const AUDIO_ERROR_EVENT = 'crusher:audio:error';

export class CrusherAudioPlayer extends LitElement {
  static styles = css`
    :host { display: block; margin-top: var(--crusher-spacing-4); }

    .player-wrap { display: flex; flex-direction: column; gap: var(--crusher-audio-stack-gap, 0); }

    h4 {
      margin: 0 0 calc(var(--crusher-spacing-2) + var(--crusher-component-border-weight) * 2);
      color: var(--crusher-audio-heading-color, var(--crusher-style-switcher-heading-color, var(--text-black-700, var(--crusher-text-secondary))));
      font-size: var(--crusher-audio-heading-font-size, var(--crusher-style-switcher-heading-font-size, var(--crusher-font-size-sm)));
      line-height: var(--crusher-audio-heading-line-height, var(--crusher-style-switcher-heading-line-height, normal));
      font-weight: var(--crusher-font-weight-semibold);
      text-transform: capitalize;
    }

    .control-panel {
      box-sizing: border-box;
      position: relative;
      background: var(--crusher-audio-panel-bg, transparent);
      border-radius: var(--crusher-radius-lg);
      border: var(--crusher-audio-panel-border, 0);
      width: var(--crusher-audio-player-width, 100%);
      height: calc(var(--crusher-spacing-10) + var(--crusher-spacing-4) + var(--crusher-spacing-1));
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 0 calc(var(--crusher-spacing-3) + var(--crusher-spacing-1));
      transition: box-shadow var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout);
      box-shadow: var(--crusher-audio-shadow, var(--outer-shadow, var(--crusher-shadow-2)));
    }

    .control-panel.playing {
      box-shadow: var(
        --crusher-audio-shadow-active,
        0 0 calc(var(--crusher-spacing-4) + var(--crusher-spacing-1))
          color-mix(in srgb, var(--crusher-text-primary) 20%, transparent)
      );
    }

    .album-art {
      position: absolute;
      left: calc(var(--crusher-spacing-4) + var(--crusher-spacing-1));
      height: var(--crusher-audio-art-size, calc(var(--crusher-spacing-10) + var(--crusher-spacing-4) + var(--crusher-spacing-1)));
      width: var(--crusher-audio-art-size, calc(var(--crusher-spacing-10) + var(--crusher-spacing-4) + var(--crusher-spacing-1)));
      border-radius: var(--crusher-radius-full);
      flex-shrink: 0;
      background-color: color-mix(in srgb, var(--crusher-color-brand-primary) 18%, var(--crusher-background-surface));
      background-image: var(--crusher-audio-art, none);
      background-size: cover;
      background-position: center;
      overflow: hidden;
      transition: transform var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout),
                  box-shadow var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout);
    }

    .album-art::after {
      content: '';
      position: absolute;
      inset: 50%;
      transform: translate(-50%, -50%);
      width: calc(var(--crusher-spacing-3) + var(--crusher-spacing-1));
      height: calc(var(--crusher-spacing-3) + var(--crusher-spacing-1));
      background: var(--crusher-color-base-white);
      border-radius: var(--crusher-radius-full);
    }

    .playing .album-art {
      animation: spin 3s linear infinite;
      box-shadow: 0 0 calc(var(--crusher-spacing-4) + var(--crusher-spacing-1))
        color-mix(in srgb, var(--crusher-text-primary) 20%, transparent);
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .play-btn {
      appearance: none;
      background: none;
      border: none;
      padding: 0;
      width: calc(var(--crusher-spacing-10) + var(--crusher-spacing-3));
      height: calc(var(--crusher-spacing-10) + var(--crusher-spacing-3));
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: inherit;
      color: var(--crusher-text-primary);
      border-radius: var(--crusher-radius-md);
      flex-shrink: 0;
      transition: color var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout),
                  background var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout);
    }

    .play-btn:hover {
      background: color-mix(in srgb, var(--crusher-color-brand-primary) 12%, var(--crusher-background-surface));
      color: var(--crusher-color-brand-primary);
    }

    .play-btn:disabled {
      cursor: not-allowed;
      opacity: var(--crusher-opacity-disabled);
    }

    .play-icon {
      display: block;
      inline-size: calc(var(--crusher-spacing-4) + var(--crusher-spacing-1));
      block-size: calc(var(--crusher-spacing-4) + var(--crusher-spacing-1));
    }

    .info-bar {
      font-size: var(--crusher-font-size-xs);
      color: var(--crusher-text-secondary);
      text-align: center;
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      transition:
        max-height var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout),
        opacity var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout);
    }

    .info-bar.visible {
      max-height: 2em;
      opacity: 1;
    }
  `;

  static properties = {
    src: { type: String },
    loop: { type: Boolean },
    heading: { type: String },
    hideHeading: { type: Boolean, attribute: 'hide-heading' },
    label: { type: String },
    art: { type: String },
    isPlaying: { type: Boolean, state: true },
    error: { type: String, state: true },
  };

  constructor() {
    super();
    this.src = '';
    this.loop = false;
    this.heading = 'Background Music';
    this.hideHeading = false;
    this.label = '';
    this.art = '';
    this.isPlaying = false;
    this.error = '';
    this._audio = null;
    this._audioHandlers = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._setupAudio();
  }

  updated(changedProperties) {
    if (changedProperties.has('src')) {
      this._setupAudio();
      return;
    }
    if (changedProperties.has('loop') && this._audio) {
      this._audio.loop = this.loop;
    }
  }

  disconnectedCallback() {
    this._teardownAudio();
    super.disconnectedCallback();
  }

  _setupAudio() {
    this._teardownAudio();
    if (!this.src) return;

    this._audio = new Audio(this.src);
    this._audio.loop = this.loop;
    this._audioHandlers = {
      play: () => {
        this.error = '';
        this.isPlaying = true;
        this._emitAudioEvent(AUDIO_PLAY_EVENT);
      },
      pause: () => {
        this.isPlaying = false;
        this._emitAudioEvent(AUDIO_PAUSE_EVENT);
      },
      ended: () => {
        this.isPlaying = false;
        this._emitAudioEvent(AUDIO_PAUSE_EVENT, { ended: true });
      },
      error: () => {
        const error = this._audio?.error?.message || 'Audio playback failed.';
        this._handlePlaybackError(error);
      },
    };

    Object.entries(this._audioHandlers).forEach(([type, handler]) => {
      this._audio.addEventListener(type, handler);
    });
  }

  _teardownAudio() {
    if (this._audio && this._audioHandlers) {
      Object.entries(this._audioHandlers).forEach(([type, handler]) => {
        this._audio.removeEventListener(type, handler);
      });
    }
    this._audio?.pause();
    this._audio = null;
    this._audioHandlers = null;
    this.isPlaying = false;
  }

  _emitAudioEvent(type, detail = {}) {
    this.dispatchEvent(new CustomEvent(type, {
      bubbles: true,
      composed: true,
      detail: {
        src: this.src,
        label: this.label,
        ...detail,
      },
    }));
  }

  _handlePlaybackError(error) {
    const message = error instanceof Error ? error.message : String(error);
    this.error = message;
    this.isPlaying = false;
    this._emitAudioEvent(AUDIO_ERROR_EVENT, { error: message });
  }

  async play() {
    if (!this._audio) this._setupAudio();
    if (!this._audio) {
      this._handlePlaybackError('Audio source is not configured.');
      return false;
    }

    try {
      await this._audio.play();
      return true;
    } catch (error) {
      this._handlePlaybackError(error);
      return false;
    }
  }

  pause() {
    this._audio?.pause();
    this.isPlaying = false;
  }

  toggle() {
    if (!this._audio) return;
    if (this._audio.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  _playIcon() {
    return html`
      <svg class="play-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M8 5.14v14l11-7-11-7z"/>
      </svg>
    `;
  }

  _pauseIcon() {
    return html`
      <svg class="play-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
      </svg>
    `;
  }

  render() {
    const artStyle = this.art ? `background-image: url(${this.art})` : '';
    const hasSource = Boolean(this.src);
    return html`
      <div class="player-wrap">
        ${!this.hideHeading && this.heading ? html`<h4>${this.heading}</h4>` : ''}
        <div class="control-panel ${this.isPlaying ? 'playing' : ''}">
          <div class="album-art ${this.isPlaying ? 'playing' : ''}" style=${artStyle}></div>
          <button type="button" class="play-btn" @click=${() => this.toggle()}
            aria-label=${this.isPlaying ? 'Pause' : 'Play'}
            aria-pressed=${this.isPlaying ? 'true' : 'false'}
            ?disabled=${!hasSource}>
            ${this.isPlaying ? this._pauseIcon() : this._playIcon()}
          </button>
        </div>
        <div class="info-bar ${this.isPlaying ? 'visible' : ''}">
          ${this.label || 'Now playing'}
        </div>
      </div>
    `;
  }
}

customElements.define('crusher-audio-player', CrusherAudioPlayer);
